import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import AvailabilityBadge from "../AvailabilityBadge";
import ContactInfo from "./ContactInfo";

interface HeroContentProps {
  parallaxOffset: number;
  onDownloadCV: () => void;
  isGenerating: boolean;
}

const HeroContent = ({ parallaxOffset, onDownloadCV, isGenerating }: HeroContentProps) => {
  const { t, language } = useLanguage();

  const tags = language === "fr" 
    ? ["Luxe & Cosmétiques", "CRM & Emailing", "E-commerce", "Gestion de projet", "Coordination"]
    : ["Luxury & Cosmetics", "CRM & Emailing", "E-commerce", "Project Management", "Coordination"];

  return (
    <div 
      className="flex-1 text-center lg:text-left min-w-0"
      style={{ transform: `translateY(${parallaxOffset}px)` }}
    >
      <div className="space-y-4 sm:space-y-5 lg:space-y-6">
        {/* Availability badges */}
        <AvailabilityBadge />
        
        {/* Role badge */}
        <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary/8 border border-primary/15 animate-fade-in">
          <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-xs sm:text-sm font-medium text-primary tracking-wide">
            {t("hero.role")}
          </span>
        </div>
        
        {/* Name */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-semibold tracking-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <span className="text-foreground">Aurélia</span>
          <br />
          <span className="text-gradient-gold">
            Ravelo
          </span>
        </h1>

        {/* Description */}
        <p 
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed animate-fade-in mx-auto lg:mx-0" 
          style={{ animationDelay: '0.2s' }}
        >
          {t("hero.description")}
        </p>

        {/* Tags */}
        <div 
          className="flex flex-wrap gap-1.5 sm:gap-2 justify-center lg:justify-start pt-1 sm:pt-2 animate-fade-in"
          style={{ animationDelay: '0.3s' }}
        >
          {tags.map((tag, i) => (
            <span
              key={tag}
              className="px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full bg-card border border-primary/15 text-xs sm:text-sm font-medium text-foreground/80 hover:border-primary/30 hover:bg-primary/5 hover:text-foreground transition-all duration-300 cursor-default"
              style={{ animationDelay: `${0.35 + i * 0.05}s` }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Contact info */}
        <ContactInfo />

        {/* CTA Button */}
        <div className="pt-4 sm:pt-6 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <Button 
            size="lg" 
            className="group relative glow-button bg-glow-gradient text-primary-foreground px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-medium shadow-luxury hover:shadow-glow transition-all duration-500 hover:-translate-y-1"
            onClick={onDownloadCV}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-2.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-2.5 group-hover:scale-110 transition-transform duration-300" />
            )}
            {isGenerating 
              ? (language === "fr" ? "Génération..." : "Generating...") 
              : t("hero.download")
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HeroContent;
